import { useState } from 'react'
import { format, parse, isValid } from 'date-fns'
import { es } from 'date-fns/locale'
import { CalendarDays, X } from 'lucide-react'

import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { t } from '@/i18n'

const ISO_FORMAT = 'yyyy-MM-dd'
const DISPLAY_FORMAT = 'dd/MM/yyyy'

function fromIso(value: string | undefined): Date | undefined {
  if (!value) return undefined
  const d = parse(value, ISO_FORMAT, new Date())
  return isValid(d) ? d : undefined
}

export interface DatePickerProps {
  /** ISO date string `yyyy-MM-dd` or empty string. */
  value: string
  onChange: (value: string) => void
  /** ISO `yyyy-MM-dd` lower bound (inclusive). */
  min?: string
  /** ISO `yyyy-MM-dd` upper bound (inclusive). */
  max?: string
  placeholder?: string
  className?: string
  ariaLabel?: string
  /** Show clear (×) button when a value is selected. Default: true. */
  clearable?: boolean
  disabled?: boolean
}

export function DatePicker({
  value,
  onChange,
  min,
  max,
  placeholder = t('common.datePlaceholder'),
  className,
  ariaLabel,
  clearable = true,
  disabled,
}: DatePickerProps) {
  const [open, setOpen] = useState(false)

  const date = fromIso(value)
  const minDate = fromIso(min)
  const maxDate = fromIso(max)
  const display = date ? format(date, DISPLAY_FORMAT, { locale: es }) : ''

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn(
            'group relative flex items-center w-full h-12 pl-9 pr-9 rounded-md border border-input bg-background text-sm md:text-base text-left',
            'hover:bg-accent/40 transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            disabled && 'opacity-50 cursor-not-allowed',
            !date && 'text-muted-foreground',
            className
          )}
        >
          <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <span className="truncate">{display || placeholder}</span>
          {clearable && date && !disabled && (
            <span
              role="button"
              tabIndex={0}
              aria-label={t('common.clearDate')}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onChange('')
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  onChange('')
                }
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-6 w-6 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground hover:bg-accent"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto min-w-[19rem] p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Calendar
          selected={date}
          minDate={minDate}
          maxDate={maxDate}
          onSelect={(d) => {
            onChange(format(d, ISO_FORMAT))
            setOpen(false)
          }}
        />
      </PopoverContent>
    </Popover>
  )
}
